import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { login as loginApi } from '../services/authService';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  function validate() {
    const errs = {};
    if (!email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = 'Invalid email format';
    if (!password) errs.password = 'Password is required';
    else if (password.length < 6) errs.password = 'Password must be at least 6 characters';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleLogin(asAdmin = false) {
    if (!validate()) return;

    setLoading(true);
    try {
      const data = await loginApi(email, password);
      if (asAdmin && data.role !== 'Admin') {
        throw new Error('This account does not have administrator access.');
      }
      const portalRole = data.role === 'Player' && data.hasVenueManagerAccess
        ? 'VenueManager' : data.role === 'Player' && data.hasBookingStaffAccess
        ? 'BookingStaff'
        : data.role === 'Player' && data.hasOpenPlayOrganiserAccess ? 'OpenPlayOrganiser' : data.role;
      if (!asAdmin && !['CourtOwner', 'VenueManager', 'BookingStaff', 'OpenPlayOrganiser'].includes(portalRole)) {
        throw new Error(data.role === 'Admin'
          ? 'Use “Login as Admin” to select a venue owner.'
          : 'Court Owner access is required for this portal.');
      }
      const user = {
        id: data.userId,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email,
        role: portalRole,
        hasOpenPlayOrganiserAccess: data.hasOpenPlayOrganiserAccess === true,
      };
      await login(data.token, user, rememberMe, data.refreshToken);
      if (asAdmin) {
        navigate('/select-owner', { replace: true });
      } else {
        toast.success('Welcome back!');
        navigate(portalRole === 'VenueManager' ? '/dashboard' : portalRole === 'BookingStaff' ? '/bookings' : portalRole === 'OpenPlayOrganiser' ? '/openplays' : from, { replace: true });
      }
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || 'Login failed. Please check your credentials.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">

        {/* Hero logo */}
        <div className="text-center">
          <img
            src="/freac-official-logo.png"
            alt="FREAC official logo"
            className="mx-auto h-auto w-64 object-contain"
          />
          <p className="mt-4 text-sm text-[#64748B] tracking-widest font-medium uppercase">
            Court Owner Portal
          </p>
        </div>

        {/* Form card */}
        <form
          className="bg-white border border-[#E5E7EB] rounded-[20px] p-8 space-y-6
                     shadow-[0_6px_18px_rgba(0,0,0,.06)]"
          onSubmit={(event) => {
            event.preventDefault();
            handleLogin(false);
          }}
        >
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[#64748B] mb-1">
              Email address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((prev) => ({ ...prev, email: '' }));
              }}
              className={`input-field mt-1 ${
                errors.email ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : ''
              }`}
              placeholder="you@example.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-[#64748B] mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors((prev) => ({ ...prev, password: '' }));
              }}
              className={`input-field mt-1 ${
                errors.password ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : ''
              }`}
              placeholder="••••••••"
            />
            {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
          </div>

          <div className="flex items-center justify-between gap-4">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-[#D0D5DD] text-[#264D59] focus:ring-[#264D59]"
              />
              <span className="text-sm text-[#64748B]">Keep me signed in</span>
            </label>
            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-[#264D59] hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full justify-center py-2.5"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Signing in...
              </span>
            ) : (
              'Sign in'
            )}
          </button>

          <div className="flex items-center gap-3" aria-hidden="true">
            <div className="h-px flex-1 bg-[#E2E8F0]" />
            <span className="text-xs font-medium uppercase tracking-wider text-[#94A3B8]">or</span>
            <div className="h-px flex-1 bg-[#E2E8F0]" />
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={() => handleLogin(true)}
            className="btn-outline w-full justify-center py-2.5"
          >
            Login as Admin
          </button>
          <p className="text-center text-xs leading-5 text-[#64748B]">
            Administrators will choose a venue owner before entering the portal.
          </p>
        </form>
      </div>
    </div>
  );
}
